import React, { useEffect, useState } from "react";
import Sidebar from "../../components/Nav/AdminSidebar";
import { FaSearch, FaTrash, FaEdit } from "react-icons/fa";

export default function RoomInfo() {
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [error, setError] = useState(null);
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // Fetch rooms and categories
  useEffect(() => {
    fetch(`${BASE_URL}/room/get-room-cat.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCategories(data.data);
      });

    fetch(`${BASE_URL}/room/getRooms.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setRooms(data.data);
          setFilteredRooms(data.data);
        } else {
          setError("Failed to load rooms");
        }
      });
  }, []);
  useEffect(() => {
    fetch(
      `${BASE_URL}/room/getRooms.php?check_in=${checkInDate}&check_out=${checkOutDate}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setRooms(data.data);
          setFilteredRooms(data.data);
        } else {
          setError("Failed to load rooms");
        }
      });
  }, [checkInDate, checkOutDate]);
  // Filter rooms when search or availability changes
  useEffect(() => {
    let filtered = rooms;

    if (searchTerm) {
      filtered = filtered.filter((room) =>
        room.room_no.toString().includes(searchTerm)
      );
    }
    if (selectedCategory) {
      filtered = filtered.filter(
        (room) => String(room.category_id) === String(selectedCategory)
      );
    }
    setFilteredRooms(filtered);
  }, [searchTerm, selectedCategory, rooms]);
  // Handle Edit Room
  const handleEdit = (room) => {
    // You can navigate to a different edit page or open a modal
    console.log("Editing room:", room);
    // Example: navigate(`/admin/edit-room/${room.room_id}`);
  };

  // Handle Delete Room
  const handleDelete = async (roomId, forceDelete = false) => {
    try {
      // Ask confirmation before any delete attempt
      if (!forceDelete) {
        const userConfirmed = window.confirm(
          "Are you sure you want to delete this room?"
        );
        if (!userConfirmed) return;
      }

      const res = await fetch(`${BASE_URL}/room/delete-room.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ room_id: roomId, forceDelete }),
      });

      const data = await res.json();

      if (data.hasFutureBookings) {
        alert("Cannot delete: This room has future bookings.");
        return;
      }

      if (data.hasPastBookings) {
        const confirmPastDelete = window.confirm(
          "This room has past bookings. Past transactions will be deleted if you proceed. Do you want to continue?"
        );
        if (!confirmPastDelete) return;

        // Retry with forceDelete=true to delete past bookings + transactions + room
        await handleDelete(roomId, true);
        return;
      }

      if (data.success) {
        alert(data.message || "Room deleted successfully.");

        // Remove deleted room from state/UI
        setRooms((prev) => prev.filter((r) => r.room_id !== roomId));
        setFilteredRooms((prev) => prev.filter((r) => r.room_id !== roomId));
      } else {
        alert(data.message || "Delete failed.");
      }
    } catch (error) {
      console.error(error);
      alert("An error occurred during deletion.");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-blue-800">Rooms</h2>
          <div className="flex flex-wrap gap-4 items-end">
            {/* Category Select */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-48 border border-gray-300 rounded-lg px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">All Room Type</option>
                {categories.map((cat) => (
                  <option key={cat.category_id} value={cat.category_id}>
                    {cat.category_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Input */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Room No.
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter room number"
                  className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 shadow-sm focus:ring focus:ring-blue-200 w-48"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Check-in Date */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check-in
              </label>
              <input
                type="date"
                value={checkInDate}
                onChange={(e) => setCheckInDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm w-48"
              />
            </div>

            {/* Check-out Date */}
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Check-out
              </label>
              <input
                type="date"
                value={checkOutDate}
                disabled={!checkInDate}
                min={
                  checkInDate
                    ? new Date(new Date(checkInDate).getTime() + 86400000)
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  const selected = e.target.value;
                  const minCheckout = new Date(
                    new Date(checkInDate).getTime() + 86400000
                  )
                    .toISOString()
                    .split("T")[0];

                  if (selected < minCheckout) {
                    setCheckOutDate(minCheckout);
                  } else {
                    setCheckOutDate(selected);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm w-48"
              />
            </div>
          </div>
        </div>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-100 text-blue-900">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Room No
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  Price (₹)
                </th>
                <th className="px-6 py-3 text-center text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredRooms.map((room) => (
                <tr key={room.room_id}>
                  <td className="px-6 py-4">{room.room_no}</td>
                  <td className="px-6 py-4">
                    {categories.find(
                      (cat) => cat.category_id == room.category_id
                    )?.category_name || "Unknown"}
                  </td>
                  <td className="px-6 py-4">
                    ₹{parseFloat(room.price).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleEdit(room)}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(room.room_id)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredRooms.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-gray-500">
                    No rooms found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
